import * as React from 'react'
import { Button } from 'primereact/button'
import { Checkbox, CheckboxChangeEvent } from 'primereact/checkbox'
import Icon from 'app/shared/components/Icon'
import { COMAGM0101, PdfType } from 'app/pages/com/agm/COMAGM0101'
import { usePopup } from '../hooks/popup'
import { getRandomNumber } from '../utils/comUtil'

export type TermItem = {
    /**
     * 표시할 개별 약관명
     */
    name: React.ReactNode
    /**
     * 개별 약관 보기를 이용할때 사용할 약관id, 없는 경우 패스함.
     */
    termId?: string
    /**
     * 개별 약관 필수 동의 여부, false or 없는 경우 선택약관
     * - 현재 case는 전부 필수, 전부 선택만 있는 상태라 약관이 1개일때만 이 값을 봄
     */
    required?: boolean
    /**
     * 개별 약관 선택 여부, true면 동의함
     */
    checked?: boolean
} & Partial<PdfType>

export type TermListProps = {
    /**
     * checkbox에 연결된 라벨에 사용될 id prefix, id는 prefix_randomseed_N 형식이됨
     */
    idPrefix?: string
    /**
     * 약관이 여러 개 일때 사용될 약관 동의 명, 단일 약관일때는 해당 약관의 name을 사용함
     */
    title?: React.ReactNode
    /**
     * 약관 리스트, 1개 뿐이면 단독 약관으로 처리한다.
     */
    items?: TermItem[]
    /**
     * 전체동의 시 다른 TermList 의 약관도 뿌려줄 경우 셋팅한다.
     */
    additionalTerms?: TermItem[]
    /**
     * 필수 여부, 값이 없으면 표시 안함. 단 약관이 1개뿐이면 TermItem.required로 판단함
     */
    required?: boolean
    /**
     * 전체동의 누를때 약관 뷰어를 띄워야하는지 여부
     * default : false
     */
    requiredTermView?: boolean
    /**
     * 전체동의 체크박스 노출여부
     */
    nonTitleCheckbox?: boolean

    /**
     * 약관동의팝업 하단 버튼명
     */
    confirmLabel?: string
    /**
     * 약관 동의 상태가 변경되면 trigger.
     * @param items props items의 checked에 값을 현재 상태로 업데이트해서 보냄
     * @param state 약관 동의 상태 items props에 보낸 index대로 true/false로 채워보냄
     * @param isAllChecked 전체 약관 동의가 완료되면 true, 그 외 false
     */
    onChange?(items: TermItem[], state: boolean[], isAllChecked: boolean): void

    /**
     * TermList 컴포넌트에서 띄운 약관뷰어팝업에서 X, 하단[동의] 버튼 클릭 시 trigger
     * @param agreed True : 동의 False : X버튼
     * @param terms 사용자가 약관 팝업에서 본 약관정보 list
     */
    onTermAgreed?: (agreed: boolean, terms: PdfType[]) => void

    /**
     * 부모 class
     */
    className?: string
}

export type TermRef = {
    isAll: boolean
    showEntireTerms: () => void
}

export const TermList = React.forwardRef<TermRef, TermListProps>(
    (props, ref) => {
        let {
            idPrefix = '',
            title,
            items: _items = [],
            additionalTerms = [],
            nonTitleCheckbox = false,
            required,
            requiredTermView = false,
            className = 'mt-16',
            confirmLabel,
            onChange,
            onTermAgreed,
        } = props
        const [items, setItems] = React.useState<TermItem[]>(_items)

        React.useImperativeHandle(
            ref,
            () => ({
                isAll: false,
                showEntireTerms: () => {
                    const filteredItems = items
                        .filter((x) => !!x.pdfUrl)
                        .filter((x) => !x.checked)
                    const filteredAdditionalItems = additionalTerms
                        .filter((x) => !!x.pdfUrl)
                        .filter((x) => !x.checked)
                    showTerm(
                        Array.of(
                            filteredItems,
                            filteredAdditionalItems
                        ).flatMap((x) => x)
                    )
                },
            }),
            [items]
        )

        //id를 생성하기 위한 고유 값, 생성이후 업데이트 되지 않는다.
        const [prefix] = React.useState(`${idPrefix}_${getRandomNumber(6)}_`)

        //check여부를 저장하는 배열 - 외부에서 넘어온 값에 따라 값이 바뀐다.
        const [checkAgree, setcheckAgree] = React.useState<boolean[]>([])
        // 동의시 항목 toggle true면 펼쳐짐, false면 닫혀짐
        const [listToggle, setListToggle] = React.useState(true)

        const [viewTermList, setViewTermList] = React.useState<TermItem[]>([])
        const termPopup = usePopup()
        const showTerm = (items: TermItem[]) => {
            items.forEach((item) => {
                if (!item.pdfNm) item.pdfNm = item.name.toLocaleString()
            })
            setViewTermList([...items])
            termPopup.open()
        }

        const handleOnAgree = (agreed: boolean) => {
            termPopup.close()

            onTermAgreed?.(agreed, viewTermList as PdfType[])
            if (agreed) {
                const newArr = checkAgree.concat()
                viewTermList.forEach((term) => {
                    newArr[
                        items.findIndex((item) => item.termId === term.termId)
                    ] = agreed
                })
                const _isAllchecked = isAllChecked(newArr)
                setcheckAgree(newArr)
                setListToggle(!_isAllchecked)
                items.forEach((item, i) => {
                    item.checked = newArr[i]
                })
                onChange(items, newArr, _isAllchecked)
            }
        }
        const handleCheckChange = (idx: number) => {
            const newArr = checkAgree.concat()
            newArr[idx] = !newArr[idx]
            const _isAllchecked = isAllChecked(newArr)
            setcheckAgree(newArr)
            setListToggle(!_isAllchecked)
            items.forEach((item, i) => {
                item.checked = newArr[i]
            })
            onChange(items, newArr, _isAllchecked)
        }

        const handleCheckAllChange = (e: CheckboxChangeEvent) => {
            const checked = e.checked
            const newArr = new Array(items.length).fill(checked)
            setcheckAgree(newArr)
            setListToggle(!checked)
            items.forEach((item) => {
                item.checked = checked
            })
            onChange(items, newArr, checked)
        }

        // 전체 동의 체크
        const isAllChecked = (arr = checkAgree) => {
            const checkedItems = arr.filter((item) => item)
            return checkedItems.length >= arr.length
        }
        /**
         * 필수/선택 라벨 생성
         */
        const requiredLabel = (() => {
            let aRequired = required
            if (items?.length === 1) {
                aRequired = items[0]?.required
            }
            if (typeof aRequired === 'undefined') {
                return ''
            } else if (aRequired) {
                return <strong className="req">필수</strong>
            } else {
                return <span className="req req-type">선택</span>
            }
        })()
        /**
         * 약관목록 대표 제목 생성, 약관이 1개뿐이면 약관명이 제목임.
         */
        const aTitle = (() => {
            let _title = title
            if (items?.length === 1) {
                _title = items[0]?.name
            }
            return _title
        })()

        React.useEffect(() => {
            const newItem = (_items ?? []).concat()
            let preCheck = true
            setcheckAgree(
                newItem.map((item) => {
                    const check = Boolean(item.checked)
                    preCheck = preCheck && check
                    return check
                })
            )
            setListToggle(!preCheck)
            setItems(newItem)
        }, [_items])

        if (!items || items?.length < 1) {
            return null
        }
        return (
            <>
                <div
                    className={`checkboxs-agree ${className} -toggle ${
                        listToggle ? '-open' : ''
                    }`.trim()}
                >
                    <div className="group">
                        <div className="checkbox-basic -big">
                            {nonTitleCheckbox ? (
                                <>
                                    <label htmlFor={prefix}>
                                        <strong>{aTitle}</strong>
                                        {requiredLabel}
                                    </label>
                                </>
                            ) : (
                                <>
                                    <Checkbox
                                        inputId={prefix}
                                        onClick={(e) => {
                                            if (isAllChecked()) {
                                                handleCheckAllChange({
                                                    checked: false,
                                                } as CheckboxChangeEvent)
                                            } else {
                                                const pdfTerms = items
                                                    .filter((x) => !!x.pdfUrl)
                                                    .filter((x) => !x.checked)
                                                if (
                                                    requiredTermView &&
                                                    pdfTerms.length > 0
                                                ) {
                                                    showTerm(
                                                        items
                                                            .filter(
                                                                (x) =>
                                                                    !!x.pdfUrl
                                                            )
                                                            .filter(
                                                                (x) =>
                                                                    !x.checked
                                                            )
                                                    )
                                                } else {
                                                    handleCheckAllChange({
                                                        checked: true,
                                                    } as CheckboxChangeEvent)
                                                }
                                            }
                                        }}
                                        checked={isAllChecked()}
                                    />
                                    <label htmlFor={prefix}>
                                        {aTitle}
                                        {requiredLabel}
                                    </label>
                                </>
                            )}
                        </div>
                        {items.length > 1 ? (
                            <Button
                                className="btn-toggle"
                                onClick={() => setListToggle((prev) => !prev)}
                            >
                                <Icon
                                    name="arrow-down"
                                    text="체크박스 항목 닫기"
                                ></Icon>
                            </Button>
                        ) : (
                            items[0]?.termId && (
                                <Button
                                    className="btn-view"
                                    label="보기"
                                    title={`${items[0]?.name} 약관보기`}
                                    onClick={() => showTerm([items[0]])}
                                />
                            )
                        )}
                    </div>
                    {items.length > 1 && (
                        <div
                            className="group-toggle"
                            aria-hidden={listToggle ? false : true}
                        >
                            <div className="list">
                                {items.map((item, idx) => {
                                    const nid = `${prefix}${idx}`
                                    return (
                                        <div className="group" key={nid}>
                                            <div className="checkbox-basic">
                                                <Checkbox
                                                    inputId={nid}
                                                    name="nid"
                                                    onClick={() => {
                                                        if (item.pdfUrl) {
                                                            if (
                                                                checkAgree[idx]
                                                            ) {
                                                                handleCheckChange(
                                                                    idx
                                                                )
                                                            } else {
                                                                showTerm([item])
                                                            }
                                                        } else {
                                                            handleCheckChange(
                                                                idx
                                                            )
                                                        }
                                                    }}
                                                    checked={checkAgree[idx]}
                                                />
                                                <label htmlFor={nid}>
                                                    {item.name}
                                                    {item.required && (
                                                        <span className="req">
                                                            필수
                                                        </span>
                                                    )}
                                                </label>
                                            </div>
                                            {item.termId && (
                                                <Button
                                                    id={`${nid}_term_button`}
                                                    className="btn-view"
                                                    label="보기"
                                                    title={`${item.name} 약관보기`}
                                                    onClick={() =>
                                                        showTerm([item])
                                                    }
                                                />
                                            )}
                                        </div>
                                    )
                                })}
                            </div>
                        </div>
                    )}
                </div>
                <COMAGM0101
                    data={viewTermList as PdfType[]}
                    visible={termPopup.state}
                    onAgree={handleOnAgree}
                    confirmLabel={confirmLabel}
                />
            </>
        )
    }
)
